import { useState } from 'react';
import { useField } from 'formik';
import { Box, Typography, Paper } from '@mui/material';
import {
  Editor,
  EditorState,
  RichUtils,
  ContentState,
  convertFromRaw,
  convertToRaw,
  DraftHandleValue,
} from 'draft-js';
import 'draft-js/dist/Draft.css';

type RawDraft = ReturnType<typeof convertToRaw>;

function toEditorState(raw: unknown): EditorState {
  try {
    if (
      raw &&
      typeof raw === 'object' &&
      (raw as any).blocks &&
      (raw as any).entityMap !== undefined
    ) {
      return EditorState.createWithContent(convertFromRaw(raw as any));
    }
  } catch {
    /* ignore malformed */
  }
  return EditorState.createWithContent(ContentState.createFromText(''));
}

export type RichTextEditorProps = {
  name: string; // Formik field name to store RawDraftContentState
  label?: string;
  disabled?: boolean;
  placeholder?: string;
  minHeight?: number;
};

export default function RichTextEditor({
  name,
  label,
  disabled,
  placeholder,
  minHeight = 140,
}: RichTextEditorProps) {
  const [field, , helpers] = useField<RawDraft | undefined>(name);

  // Draft.js local state (same pattern as docs)
  const [editorState, setEditorState] = useState<EditorState>(() =>
    toEditorState(field.value),
  );

  // Docs style onChange: update local state
  const onChange = (next: EditorState) => {
    setEditorState(next);
    // Also mirror to Formik as RawDraftContentState
    helpers.setValue(convertToRaw(next.getCurrentContent()));
  };

  // Docs style key command handling: enables ⌘B / ⌘I etc.
  const handleKeyCommand = (
    command: string,
    state: EditorState,
  ): DraftHandleValue => {
    const newState = RichUtils.handleKeyCommand(state, command);
    if (newState) {
      onChange(newState);
      return 'handled';
    }
    return 'not-handled';
  };

  return (
    <Box>
      {label && (
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          {label}
        </Typography>
      )}

      <Paper
        variant="outlined"
        sx={{
          p: 1,
          minHeight,
          '& .public-DraftEditor-content': { minHeight: minHeight - 24 },
        }}>
        <div dir="ltr" style={{ unicodeBidi: 'plaintext' }}>
          <Editor
            editorState={editorState}
            onChange={onChange}
            handleKeyCommand={handleKeyCommand}
            readOnly={!!disabled}
            placeholder={placeholder}
            spellCheck
          />
        </div>
      </Paper>
    </Box>
  );
}
